// Header — logo, in-flight counts, queue-mode display, theme + settings buttons.

import { useOpenRouterCredits } from '../hooks/useOpenRouterCredits';
import type { OpenRouterCreditsResponse, QueueMode, RunVM } from '../lib/types';

interface HeaderProps {
  runs: RunVM[];
  /** Queue mode read from /config; null while the request is in-flight. */
  queueMode: QueueMode | null;
  /** providers_configured from /config; controls which provider badges render. */
  providersConfigured: string[];
  theme: 'dark' | 'light';
  toggleTheme: () => void;
  onSettings: () => void;
}

export function Header(props: HeaderProps) {
  const counts = {
    implementing: props.runs.filter((r) => r.column === 'implementing').length,
    review: props.runs.filter((r) => r.column === 'review').length,
    pr: props.runs.filter((r) => r.column === 'pr').length,
  };
  const openRouterConfigured = props.providersConfigured.includes('openrouter');
  return (
    <header className="header">
      <div className="header-left">
        <div className="logo-mark">
          <span className="logo-dot" />
          <span className="logo-text mono">acpp / ops</span>
        </div>
        <div className="header-stats mono">
          <span><b>{counts.implementing}</b> implementing</span>
          <span className="dot-sep">·</span>
          <span><b>{counts.review}</b> reviewing</span>
          <span className="dot-sep">·</span>
          <span><b>{counts.pr}</b> awaiting merge</span>
        </div>
      </div>
      <div className="header-right">
        {openRouterConfigured && <OpenRouterCreditsPill />}
        {props.queueMode && (
          <span className="mono dim queue-mode" title="QUEUE_MODE on the review-loop server">
            {props.queueMode}
          </span>
        )}
        <button
          className="icon-btn"
          onClick={props.toggleTheme}
          aria-label="toggle theme"
          title={props.theme === 'dark' ? 'switch to light' : 'switch to dark'}
        >
          {props.theme === 'dark' ? (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path
                d="M13 9.5A5.5 5.5 0 0 1 6.5 3a.5.5 0 0 0-.7-.45A6 6 0 1 0 13.45 10.2a.5.5 0 0 0-.45-.7Z"
                fill="currentColor"
              />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.3" />
              <path
                d="M8 1.5v1.5M8 13v1.5M14.5 8H13M3 8H1.5M12.6 3.4l-1.05 1.05M4.45 11.55 3.4 12.6M12.6 12.6l-1.05-1.05M4.45 4.45 3.4 3.4"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
          )}
        </button>
        <button className="icon-btn" onClick={props.onSettings} aria-label="settings" title="configure">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path
              d="m13.4 9.6.9.7a.6.6 0 0 1 .14.78l-1.2 2.08a.6.6 0 0 1-.74.27l-1.05-.42a4.6 4.6 0 0 1-1 .58l-.16 1.12a.6.6 0 0 1-.6.51H7.31a.6.6 0 0 1-.6-.51l-.16-1.12a4.6 4.6 0 0 1-1-.58l-1.05.42a.6.6 0 0 1-.74-.27l-1.2-2.08a.6.6 0 0 1 .14-.78l.9-.7a4.7 4.7 0 0 1 0-1.2l-.9-.7a.6.6 0 0 1-.14-.78l1.2-2.08a.6.6 0 0 1 .74-.27l1.05.42c.31-.23.65-.43 1-.58l.16-1.12a.6.6 0 0 1 .6-.51h2.4a.6.6 0 0 1 .6.51l.16 1.12c.35.15.69.35 1 .58l1.05-.42a.6.6 0 0 1 .74.27l1.2 2.08a.6.6 0 0 1-.14.78l-.9.7c.04.4.04.8 0 1.2Z"
              stroke="currentColor"
              strokeWidth="1.1"
              strokeLinejoin="round"
            />
            <circle cx="8.5" cy="9" r="1.8" stroke="currentColor" strokeWidth="1.1" />
          </svg>
        </button>
        {/*
          CF Access sign-out. Hitting /cdn-cgi/access/logout on the protected
          hostname (console.ten3t.com) clears the CF_Authorization cookie for
          this app and bounces back to the configured app launcher / login
          page. The next /api/* call will trigger a fresh GitHub OAuth round.
          Renders as a top-level anchor so the browser does a full-page nav
          (CF Access logout requires it; XHR follows would 302-error).
        */}
        <a
          className="icon-btn"
          href="/cdn-cgi/access/logout"
          aria-label="sign out"
          title="sign out of Cloudflare Access"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
            <path
              d="M10.5 5l3 3-3 3M13.5 8H6.5"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>
    </header>
  );
}

// Warn when less than 10% of the limit remains; render in red when the
// underlying request failed. Self-hides while the first fetch is in-flight
// to avoid a layout flash.
const WARN_THRESHOLD_FRACTION = 0.1;

function OpenRouterCreditsPill() {
  const { credits, error } = useOpenRouterCredits();
  if (!credits && !error) return null;
  if (credits?.state === 'unconfigured') return null;

  if (credits?.state === 'error' || (error && !credits)) {
    const reason = credits?.error ?? error ?? 'request failed';
    return (
      <span
        className="mono credits-pill credits-pill-error"
        title={`OpenRouter credits unavailable: ${reason}`}
      >
        OR err
      </span>
    );
  }

  if (credits?.state === 'ok') {
    const { usage, limit, limit_remaining } = credits;
    const label = formatCreditsLabel(usage, limit, limit_remaining);
    const tip = formatCreditsTitle(usage, limit, limit_remaining, credits.checked_at);
    const lowOnCredits =
      limit !== null && limit > 0 && limit_remaining !== null &&
      limit_remaining / limit < WARN_THRESHOLD_FRACTION;
    const className = lowOnCredits
      ? 'mono credits-pill credits-pill-warn'
      : 'mono dim credits-pill';
    return <span className={className} title={tip}>{label}</span>;
  }

  return null;
}

function formatCreditsLabel(
  usage: number | null,
  limit: number | null,
  remaining: number | null,
): string {
  if (limit !== null && remaining !== null) {
    return `OR ${formatUsd(remaining)} left`;
  }
  if (usage !== null) {
    return `OR ${formatUsd(usage)} used`;
  }
  return 'OR —';
}

function formatCreditsTitle(
  usage: number | null,
  limit: number | null,
  remaining: number | null,
  checkedAt: string | null,
): string {
  const lines: string[] = ['OpenRouter credits'];
  if (usage !== null) lines.push(`Used: ${formatUsd(usage)}`);
  if (limit !== null) lines.push(`Limit: ${formatUsd(limit)}`);
  if (remaining !== null) lines.push(`Remaining: ${formatUsd(remaining)}`);
  if (limit === null && usage !== null) lines.push('(no spending cap on this key)');
  if (checkedAt) lines.push(`Checked: ${new Date(checkedAt).toLocaleTimeString()}`);
  return lines.join('\n');
}

function formatUsd(n: OpenRouterCreditsResponse['usage']): string {
  if (n === null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 100) return `$${n.toFixed(0)}`;
  if (Math.abs(n) >= 1) return `$${n.toFixed(2)}`;
  // Sub-cent usage on a really cheap model would otherwise round to
  // `$0.000` and read as "no spend yet". Show the threshold so the badge
  // means "nonzero but tiny", not zero.
  if (n > 0 && n < 0.001) return '<$0.001';
  return `$${n.toFixed(3)}`;
}
